import { usePageTitle } from "@/hooks/usePageTitle";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LearnMoreModal } from "@/components/LearnMoreModal";
import { SocialLinks } from "@/components/SocialLinks";
import {
  Activity, Microscope, Dumbbell, Users, Apple, LineChart,
  ArrowRight, Check, X, QrCode, Wallet, FileText, Quote, Heart, UserX,
  BookOpen, ClipboardList, FolderOpen, Zap, ChefHat, Leaf, Pill, Mail
} from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/f9e20068-ad13-476d-954f-2d8b0325eeb4/images/246202ebd95e449fd5438e2b37076bc17d4a1f94800de438f4c436754e8ee04c.png";
const ANDREA = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/rt3xmc5g_IMG_0260.jpg";
const NT_IMG = "https://images.pexels.com/photos/15391542/pexels-photo-15391542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GYM = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/bmdl0sn8_scales-woman.jpeg";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const benefits = [
  { icon: Microscope, title: "Precision, not guesswork", text: "Your own biological data replaces generic one-size-fits-all systems." },
  { icon: Dumbbell, title: "Body composition, not weight", text: "Focus on retaining lean muscle mass — improve metabolic rate." },
  { icon: Users, title: "Professional access", text: "Get access to a qualified Nutritional Therapist, both via the online membership, and in-person when the physical classes launch in 2027." },
  { icon: Apple, title: "Science-based nutrition", text: "Whole foods first — never 'free foods' or ultra-processed shortcuts." },
  { icon: LineChart, title: "Data-driven accountability", text: "Using weekly BIA data, we monitor the physiological markers that matter." },
  { icon: Activity, title: "Metabolic physiology", text: "Using modern, evidence-based protocols which are proven to provide lasting results." },
];

const FOOD_GALLERY = [
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/263gex1g_IMG_0041.jpeg", alt: "Rainbow beetroot salad", caption: "Rainbow salads" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/xzbvawc1_IMG_1710.jpeg", alt: "High-protein egg muffins", caption: "High-protein bakes" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/wo1o2a7n_IMG_3607.jpeg", alt: "Wholegrain oat flapjacks", caption: "Smarter sweet treats" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/4ew663iy_IMG_3616.jpeg", alt: "Fresh blueberries", caption: "Nutrient-dense whole foods" },
];

const memberAccess = [
  { icon: BookOpen, title: "The Member Handbook", text: "A complete 35-page guide walking you through the 4 Pillars program." },
  { icon: ClipboardList, title: "Progress tracker & menu planner", text: "Keep yourself consistent, week to week." },
  { icon: FolderOpen, title: "A full content library", text: "Recipes, cheat sheets, menu ideas and educational PDFs." },
  { icon: Users, title: "The private member Facebook group", text: "Questions, support and genuine community — your direct access to a qualified and experienced Nutritional Therapist." },
  { icon: Zap, title: "The Athlete Hub", text: "Extra guidance for anyone balancing training and performance with body composition goals." },
];

const newsletterItems = [
  { icon: ChefHat, title: "A brand-new recipe", text: "" },
  { icon: Microscope, title: "The latest research", text: "Nutrition for fat loss, health and longevity — plus a deeper dive into one of the 4 Pillars." },
  { icon: Leaf, title: "Superfood of the week", text: "With tips on how to incorporate it into your diet." },
  { icon: Pill, title: "Supplement information", text: "Straightforward, evidence-based guidance — what's worth taking, what to avoid, and which brands are actually backed by science. No guesswork, no gimmicks." },
];

const compare = [
  ["Basis of progress", "Body composition + metabolic markers", "Weight on a scale alone"],
  ["Measurement", "BIA data", "Standard weigh-in"],
  ["Nutrition model", "Whole foods, Nutritionally Balanced", "'Free foods' / points / UPFs"],
  ["'Stayed the same' weeks", "Explained by lean muscle gain", "Frustrating & unexplained weight loss plateau, leads to further restrictions"],
  ["Professional input", "Qualified Nutritional Therapist", "Volunteer-led groups"],
];

const forYou = [
  { title: "You are done with \"guesswork\"", text: "You want to understand the data—like body fat percentage and visceral fat levels—so you can make informed decisions that actually move the needle on your health." },
  { title: "You value longevity over aesthetics", text: "You want to build a body that is strong, resilient, and fueled correctly for the long haul, rather than just \"getting thin\" for a fleeting event, and avoiding the rebound." },
  { title: "You are seeking a sustainable solution", text: "You want to learn how to balance your blood sugar and hormones through real, delicious food that fits into a busy, active life." },
  { title: "You thrive in community", text: "You appreciate the power of accountability, shared knowledge, and having a supportive group of like-minded women who are all navigating the same season of life." },
];
const notForYou = [
  { title: "You are looking for a \"quick fix\"", text: "If your goal is to lose a stone in two weeks, this isn't the right place. We prioritize sustainable metabolic changes that last for years, not days." },
  { title: "You want a restrictive \"crash diet\"", text: "We don't believe in starving yourself or cutting out entire food groups. If you are looking for a plan that forces you to survive on juices or tiny calorie counts, you won't find that here." },
  { title: "You are unwilling to look at the \"hidden\" numbers", text: "If you aren't open to learning about body composition and metabolic health, and strictly want to chase a lower number on the scale regardless of the internal cost, our data-led approach will likely frustrate you." },
  { title: "You prefer to work in complete isolation", text: "We are a community-led program. If you are looking for a silent, solo project without group interaction or coaching calls, our high-engagement club format won't be the right fit." },
];

const testimonials = [
  { name: "Chris", role: "Member", text: "For the first time I'm not being told to count calories and eat less. I honestly didn't believe I could lose weight whilst eating such filling meals but in the first week I had lost 3lb. The meals are very satisfying, filling, healthy and sustainable. Andrea is very knowledgeable and answers any questions with facts which make sense. In addition to the weight loss I also feel healthier. I suffer from chronic illness and have noticed that my body is moving more freely and I have lost the bloating." },
  { name: "Jenny", role: "PT", text: "Since I have started the MBBC I have lost just under 2kg in 2 weeks, reduced my visceral body fat and body fat % and increased muscle mass. I feel so much better and have no sugar cravings. My pain level has decreased as a sufferer from fibromyalgia, I sleep better and have less bloating and more energy. This is so easy to follow as it's not a restricted plan and I actually look forward to having carbs at dinner now, like a treat. It's great! Andrea is so supportive, very experienced and knowledgeable in nutrition and has a lovely calm energy." },
  { name: "Vicky", role: "Member", text: "I have never dieted or followed an eating plan before but at 50+ it became something I wanted to try. Why? Because of the tiredness, afternoon slumps, brain fog and a general feeling of more fat, less muscle. After two weeks I feel more myself and have found the changes to what I eat easy to follow. The simplicity of the plan helps, the food tastes good, you stay full for longer and all the \"why's\" are improving." },
];

const VIRTUAL_OPEN = true;
const plans = [
  { id: "virtual", name: "Virtual", price: 18, tag: "Online", available: VIRTUAL_OPEN, popular: true, comingLabel: "Opens 1st September", features: ["Menu ideas & recipes", "Nutrition teaching library", "Community access", "Weekly emails with deeper dives into nutrition and health plus new recipes and cookery tips"] },
  { id: "physical", name: "Physical — In person", price: 28, tag: "In-person coaching", available: false, comingLabel: "Coming early 2027", features: ["Local in-person classes", "BIA body-composition testing", "Therapist-led coaching", "Full digital content library"] },
  { id: "hybrid", name: "Hybrid", price: 38, tag: "Best of both", available: false, comingLabel: "Coming early 2027", features: ["All the benefits of Virtual and Physical memberships"] },
];

export default function Landing() {
  usePageTitle(null);
  const [learnOpen, setLearnOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative">
        <div className="brand-blob bg-teal" style={{ width: 420, height: 420, top: -80, right: -60 }} />
        <div className="brand-blob bg-navy" style={{ width: 360, height: 360, bottom: -120, left: -80, opacity: 0.15 }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.span variants={fade} initial="hidden" animate="show"
              className="text-xs uppercase tracking-[0.2em] font-semibold text-navy/70">
              Health · Nutrition · Body Composition
            </motion.span>
            <motion.h1 variants={fade} custom={1} initial="hidden" animate="show"
              className="mt-5 font-heading text-5xl sm:text-6xl tracking-tight leading-[1.05] text-navy">
              Leaner. Healthier. <span className="text-teal">Happier.</span>
            </motion.h1>
            <motion.p variants={fade} custom={2} initial="hidden" animate="show"
              className="mt-6 text-lg text-[#57534E] leading-relaxed max-w-lg">
              My Best Body Club goes beyond weight alone. Using Bio-Impedance Analysis data — from home
              scales, equipment available at some gyms and pharmacies, or via our member classes starting
              2027 — you get a true picture of your metabolic health: body fat %, visceral fat and hydration.
              With expert guidance from a qualified and experienced Nutritional Therapist, membership gives
              you all the tools and knowledge you need to lose body fat, and just as importantly, prevent it
              from returning.
            </motion.p>
            <motion.div variants={fade} custom={3} initial="hidden" animate="show" className="mt-9 flex flex-wrap gap-4">
              <Link to="/join" data-testid="hero-join-client"
                className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-dark transition-colors rounded-full px-8 py-3.5 font-medium">
                Become a Member <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/therapists" data-testid="hero-become-nt"
                className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark transition-colors rounded-full px-8 py-3.5 font-medium">
                Become a Therapist
              </Link>
              <button onClick={() => setLearnOpen(true)} data-testid="hero-learn-more"
                className="inline-flex items-center gap-2 border border-navy/25 text-navy hover:bg-navy/5 transition-colors rounded-full px-8 py-3.5 font-medium">
                Learn More
              </button>
            </motion.div>
          </div>
          <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={HERO} alt="Body composition analysis and health data" className="w-full h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-[#E7E5E4] p-5 w-56">
              <div className="flex items-center gap-2 text-teal"><LineChart className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-wide">BIA Check-in</span></div>
              <p className="mt-2 text-2xl font-heading font-semibold text-navy">+1.4kg lean</p>
              <p className="text-xs text-[#78716C]">muscle mass this month</p>
            </div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[#E7E5E4] bg-white py-4 overflow-hidden">
          <div className="marquee text-sm font-medium text-navy/60">
            {[..."x".repeat(2)].map((_, i) => (
              <span key={`marquee-copy-${i}`} className="flex">
                {["Fat Loss", "Metabolic Physiology", "Data-Driven Accountability", "Whole-Food Nutrition", "Lean Muscle Retention", "Bioelectrical Impedance (BIA)", "Professional Guidance"].map((t) => (
                  <span key={t} className="mx-8 flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-teal" />{t}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Science / Benefits */}
      <section id="science" className="py-24 sm:py-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">The MBBC Difference</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">
              Built on Biology, not simply a number on the scales.
            </h2>
            <p className="mt-4 text-[#57534E] leading-relaxed">
              Bioelectrical Impedance Analysis (BIA) measures the metabolic markers that actually
              determine health and longevity — finally answering those frustrating "stayed the same" weeks.
            </p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div key={b.title} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center text-teal">
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-heading text-xl text-navy">{b.title}</h3>
                <p className="mt-2 text-sm text-[#57534E] leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Food gallery strip */}
      <section className="pb-24 px-6 lg:px-8" data-testid="food-gallery-strip">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {FOOD_GALLERY.map((f, i) => (
              <motion.figure key={f.src} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="group">
                <div className="rounded-2xl overflow-hidden shadow-md aspect-square">
                  <img src={f.src} alt={f.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <figcaption className="mt-2 text-xs text-[#78716C] text-center">{f.caption}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 px-6 lg:px-8 bg-navy text-white relative overflow-hidden">
        <div className="brand-blob bg-teal" style={{ width: 380, height: 380, top: -100, right: -80 }} />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Why members switch</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight">A modern model rooted in metabolic physiology</h2>
          </div>
          <div className="mt-14 overflow-hidden rounded-2xl border border-white/10">
            <div className="grid grid-cols-3 bg-white/5 text-sm font-heading">
              <div className="p-5 text-white/50"></div>
              <div className="p-5 font-semibold text-teal flex items-center gap-2"><Check className="w-4 h-4" /> My Best Body Club</div>
              <div className="p-5 font-semibold text-white/60">Traditional Clubs</div>
            </div>
            {compare.map((row, i) => (
              <div key={row[0]} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                <div className="p-5 text-white/70 font-medium">{row[0]}</div>
                <div className="p-5 text-white flex items-start gap-2"><Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />{row[1]}</div>
                <div className="p-5 text-white/50 flex items-start gap-2"><X className="w-4 h-4 text-white/30 mt-0.5 shrink-0" />{row[2]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member access */}
      <section className="pt-24 sm:pt-32 pb-5 px-6 lg:px-8" data-testid="member-access-section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">What's included</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">As a member, you'll get access to</h2>
          </div>
          <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-4">
              {memberAccess.map((m, i) => (
                <motion.div key={m.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="flex items-start gap-4 bg-white border border-[#E7E5E4] rounded-2xl p-5 shadow-sm">
                  <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0"><m.icon className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-heading text-lg text-navy">{m.title}</h3>
                    <p className="mt-1 text-sm text-[#57534E] leading-relaxed">{m.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="bg-navy text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden lg:sticky lg:top-28" data-testid="weekly-newsletter-card">
              <div className="brand-blob bg-teal" style={{ width: 260, height: 260, top: -70, right: -50 }} />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-teal">
                  <Mail className="w-4 h-4" /> The part that keeps giving, week after week
                </span>
                <h3 className="mt-3 font-heading text-2xl sm:text-3xl">Every member receives a weekly newsletter</h3>
                <ul className="mt-7 space-y-5">
                  {newsletterItems.map((n) => (
                    <li key={n.title} className="flex items-start gap-3.5">
                      <div className="w-9 h-9 rounded-lg bg-white/10 text-teal flex items-center justify-center shrink-0"><n.icon className="w-4 h-4" /></div>
                      <div>
                        <p className="font-semibold">{n.title}</p>
                        {n.text && <p className="mt-0.5 text-sm text-white/70 leading-relaxed">{n.text}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
          <div className="mt-16 text-center max-w-2xl mx-auto">
            <p className="font-heading text-xl sm:text-2xl text-navy">
              This isn't just another diet, and it's not a one-off download. It's ongoing, evolving support —
              built to grow with you.
            </p>
            <Link to="/join" data-testid="member-access-cta"
              className="mt-7 inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-full px-8 py-3.5 text-sm font-semibold transition-colors">
              Become a member <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Inner Circle promo */}
      <section className="py-20 px-6 lg:px-8 bg-[#F5F5F4]" data-testid="inner-circle-promo">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="bg-navy text-white rounded-[2rem] p-8 sm:p-12 relative overflow-hidden grid md:grid-cols-[auto_1fr] gap-10 items-center"
            style={{ border: "2px solid #C9A227" }}>
            <div className="brand-blob" style={{ width: 300, height: 300, top: -90, right: -70, background: "#C9A227", opacity: 0.22 }} />
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/371pmp6w_Inner%20Circle%20Logo.png"
              alt="MBBC Inner Circle" className="hidden md:block w-56 h-auto rounded-2xl bg-white p-4 relative" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">Need a bit more support on your journey?</h2>
              <p className="mt-4 text-white/80 leading-relaxed">
                That's what our <span className="font-semibold" style={{ color: "#E8CB6B" }}>Inner Circle</span> is
                for. It's 1-1 coaching that sits alongside your membership — a space where every session is shaped
                around you, your progress and what's actually going on in your life right now. For members who'd
                love a bit more closeness and accountability on the journey.
              </p>
              <p className="mt-3 text-white/80 leading-relaxed">
                Places are strictly limited — just 6 spots per 8-week course, to keep it genuinely personal.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Link to="/inner-circle" data-testid="inner-circle-promo-cta"
                  className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold text-navy hover:opacity-90 transition-opacity"
                  style={{ background: "#E8CB6B" }}>
                  Discover Inner Circle <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="https://calendly.com/andreawood120/discovery-call?month=2026-09" target="_blank" rel="noopener noreferrer"
                  data-testid="home-discovery-call-link"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold border text-white hover:bg-white/10 transition-colors"
                  style={{ borderColor: "#E8CB6B" }}>
                  Book a 15-minute discovery call
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 sm:py-32 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl bg-white p-3">
              <img src={ANDREA} alt="Andrea Wood, Nutritional Therapist" className="w-full h-auto rounded-[1.5rem]" />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-teal text-white rounded-2xl px-5 py-4 shadow-xl">
              <p className="font-heading text-lg leading-tight">Andrea Wood</p>
              <p className="text-xs text-white/80">Nutritional Therapist · Founder</p>
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">About MBBC</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">
              Meet the founder behind My Best Body Club
            </h2>
            <p className="mt-5 text-[#57534E] leading-relaxed">
              My Best Body Club was founded by <strong className="text-navy">Andrea Wood</strong>, a qualified
              Nutritional Therapist on a mission to move the conversation beyond the scales. By combining
              Bioelectrical Impedance Analysis (BIA) with whole-food nutrition, Andrea helps
              members focus on the bigger picture — lean muscle, metabolic health and lasting results.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/about" data-testid="about-link"
                className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-dark transition-colors rounded-full px-7 py-3 font-medium">
                Read our full story <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex items-center gap-3">
                <SocialLinks variant="light" />
                <span className="text-sm text-[#78716C]">Follow Andrea</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who is MBBC for / not for */}
      <section className="py-20 px-6 lg:px-8 bg-[#F5F5F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Is it right for you?</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">Who My Best Body Club™ is — and isn't — for</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E7E5E4] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center"><Heart className="w-5 h-5" /></div>
                <h3 className="font-heading text-xl text-navy">Who is My Best Body Club™ for?</h3>
              </div>
              <p className="mt-5 text-sm text-[#57534E] leading-relaxed">
                This program is for women over 45 who are ready to move beyond the scales alone and focus on what truly
                dictates your metabolic health and vitality. It is for you if:
              </p>
              <ul className="mt-5 space-y-4">
                {forYou.map((f) => (
                  <li key={f.title} className="flex items-start gap-2.5 text-sm text-[#44403C]">
                    <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                    <span><strong className="text-navy">{f.title}:</strong> {f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[#E7E5E4] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-stone-100 text-stone-500 flex items-center justify-center"><UserX className="w-5 h-5" /></div>
                <h3 className="font-heading text-xl text-navy">Who is this NOT for?</h3>
              </div>
              <p className="mt-5 text-sm text-[#78716C] leading-relaxed">
                To ensure the best experience for everyone in the group, it's important to be clear about what
                My Best Body Club™ is not. This program is not for you if:
              </p>
              <ul className="mt-5 space-y-4">
                {notForYou.map((f) => (
                  <li key={f.title} className="flex items-start gap-2.5 text-sm text-[#78716C]">
                    <X className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
                    <span><strong className="text-stone-600">{f.title}:</strong> {f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Dual journeys */}
      <section className="py-24 sm:py-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
          <div className="rounded-[2rem] overflow-hidden relative group">
            <img src={GYM} alt="Member" className="w-full h-full min-h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            <div className="absolute bottom-0 p-8 text-white">
              <h3 className="font-heading text-3xl">For Members</h3>
              <p className="mt-2 text-white/80 max-w-sm">Data-driven nutritional protocols with a member content library of recipes and educational PDFs. You'll receive weekly emails with deeper dives into the MBBC 4 core principles, in addition to cooking and meal prep tips, and advanced nutrition and health information.</p>
              <Link to="/join" data-testid="journey-client"
                className="mt-5 inline-flex items-center gap-2 bg-white text-navy rounded-full px-6 py-3 font-medium hover:bg-white/90 transition-colors">
                Become a member <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="rounded-[2rem] overflow-hidden relative">
            <img src={NT_IMG} alt="Nutritional Therapist" className="w-full h-full min-h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-dark via-teal-dark/40 to-transparent" />
            <div className="absolute bottom-0 p-8 text-white">
              <h3 className="font-heading text-3xl">For Nutritional Therapists</h3>
              <p className="mt-2 text-white/85 max-w-sm">Run local classes, keep 80% of every fee, and manage clients, venues & earnings from one dashboard.</p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> 80% revenue</span>
                <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5" /> QR sign-ups</span>
                <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Monthly statements</span>
              </div>
              <Link to="/therapists" data-testid="journey-nt"
                className="mt-5 inline-flex items-center gap-2 bg-white text-teal-dark rounded-full px-6 py-3 font-medium hover:bg-white/90 transition-colors">
                Join the team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 lg:px-8 bg-navy text-white relative overflow-hidden">
        <div className="brand-blob bg-teal" style={{ width: 360, height: 360, bottom: -100, left: -60 }} />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Member stories</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight">Real results, real motivation</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="bg-white/5 border border-white/10 rounded-2xl p-7">
                <Quote className="w-8 h-8 text-teal" />
                <p className="mt-4 text-white/85 leading-relaxed text-sm">"{t.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal text-white flex items-center justify-center font-heading text-sm">{t.name.charAt(0)}</div>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 lg:px-8 bg-[#F5F5F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Membership</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">Choose how you want to be coached</h2>
            <p className="mt-4 text-[#57534E]">Our Virtual membership is open now — Physical and Hybrid launch early 2027.</p>
            <button onClick={() => setLearnOpen(true)} data-testid="pricing-learn-more"
              className="mt-6 inline-flex items-center gap-2 border border-teal text-teal hover:bg-teal hover:text-white transition-colors rounded-full px-7 py-3 font-medium">
              Learn More <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4 text-sm text-[#78716C]">
              Got questions? <Link to="/faq" data-testid="pricing-faq-link" className="text-teal font-medium hover:underline">Read the FAQs</Link>
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {plans.map((p, i) => (
              <motion.div key={p.id} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className={`relative bg-white rounded-2xl p-8 border ${p.popular ? "border-teal shadow-xl scale-[1.02]" : "border-[#E7E5E4] shadow-sm"} ${!p.available ? "opacity-90" : ""}`}>
                {p.available && p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal text-white text-xs font-semibold px-4 py-1 rounded-full">Available now</span>}
                {!p.available && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-white text-xs font-semibold px-4 py-1 rounded-full ${p.id === "virtual" ? "bg-teal" : "bg-navy"}`}>{p.comingLabel}</span>}
                <h3 className="font-heading text-2xl text-navy">{p.name}</h3>
                <p className="text-sm text-[#78716C]">{p.tag}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-heading text-5xl text-navy">£{p.price}</span>
                  <span className="text-[#78716C] mb-1.5">/month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#44403C]">
                      <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                {p.available ? (
                  <Link to={`/join?plan=${p.id}`} data-testid={`pricing-${p.id}`}
                    className={`mt-8 block text-center rounded-full px-6 py-3 font-medium transition-colors ${p.popular ? "bg-navy text-white hover:bg-navy-dark" : "border border-[#E7E5E4] text-navy hover:bg-[#F5F5F4]"}`}>
                    Get started
                  </Link>
                ) : p.id === "virtual" ? (
                  <button onClick={() => setLearnOpen(true)} data-testid={`pricing-${p.id}`}
                    className="mt-8 block w-full text-center rounded-full px-6 py-3 font-medium bg-teal text-white hover:bg-teal-dark transition-colors">
                    Join the waitlist
                  </button>
                ) : (
                  <span data-testid={`pricing-${p.id}`}
                    className="mt-8 block text-center rounded-full px-6 py-3 font-medium bg-[#F5F5F4] text-[#A8A29E] cursor-not-allowed">
                    {p.comingLabel}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-10 bg-navy text-white rounded-2xl p-7 sm:p-8 relative overflow-hidden flex flex-wrap items-center justify-between gap-6"
            data-testid="inner-circle-banner" style={{ border: "2px solid #C9A227" }}>
            <div className="brand-blob" style={{ width: 240, height: 240, top: -70, right: -50, background: "#C9A227", opacity: 0.2 }} />
            <div className="relative max-w-2xl">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#E8CB6B" }}>New · Only 6 spots available</span>
              <h3 className="mt-2 font-heading text-2xl sm:text-3xl">MBBC Inner Circle</h3>
              <p className="mt-2 text-white/75 text-sm leading-relaxed">
                A private, guided 8-week journey through the 4 Pillars — weekly 1-1 sessions with Andrea,
                bespoke plans and full platform access.
              </p>
            </div>
            <Link to="/inner-circle" data-testid="inner-circle-banner-cta"
              className="relative inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold text-navy hover:opacity-90 transition-opacity"
              style={{ background: "#E8CB6B" }}>
              Discover Inner Circle <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <LearnMoreModal open={learnOpen} onClose={() => setLearnOpen(false)} />
      <Footer />
    </div>
  );
}
